import { parseAccountIdsFromString } from '@/helpers/parseAccountIdsFromString';
import { createAccountIdsValidator } from '@/helpers/validateAccountIds';
import { useCreateBulkPaymentLinksMutation, useGetBulkPaymentLinkJobsQuery } from '@/services/bulkPaymentLinks';
import type { BulkPaymentLinkJobSummary } from '@/services/bulkPaymentLinks';
import { isErrorWithData, downloadFile } from '@/services/helpers';
// Uses legacy palmetto-components + Formik to mirror BillingBlockAdmin (sibling admin page).
import {
  Badge,
  Box,
  Button,
  FormikRadioGroup,
  FormikTextareaInput,
  FormikTextInput,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { object, string, number, mixed } from 'yup';

import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

interface BulkPaymentLinkFormData {
  accountIds: string;
  deliveryMethod: 'email' | 'sms' | 'linkOnly' | 'consumerLink';
  expirationDays: string;
}

const initialValues: BulkPaymentLinkFormData = {
  accountIds: '',
  deliveryMethod: 'email',
  expirationDays: '',
};

const MAX_ACCOUNT_IDS = 1000;

const formSchema = object().shape({
  accountIds: string()
    .required('Account IDs are required')
    .test('is-valid-mongo-ids', 'Invalid account IDs', createAccountIdsValidator())
    .test('max-account-ids', `You can enter at most ${MAX_ACCOUNT_IDS} account IDs`, async (value) => {
      if (!value) {
        return true;
      }
      const ids = (await parseAccountIdsFromString(value)).filter((id) => id.trim().length > 0);
      return ids.length <= MAX_ACCOUNT_IDS;
    }),
  deliveryMethod: string()
    .required('Delivery method is required')
    .oneOf(['email', 'sms', 'linkOnly', 'consumerLink']),
  expirationDays: mixed().when('deliveryMethod', {
    is: 'consumerLink',
    then: (schema) => schema.notRequired(),
    otherwise: () =>
      number()
        .typeError('Expiration days is required')
        .required('Expiration days is required')
        .integer('Must be a whole number')
        .min(1, 'Must be at least 1 day')
        .max(30, 'Must be no more than 30 days'),
  }),
});

const deliveryMethodOptions = [
  { id: 'email', value: 'email', label: 'Email' },
  { id: 'sms', value: 'sms', label: 'SMS' },
  { id: 'linkOnly', value: 'linkOnly', label: 'Link Only' },
  { id: 'consumerLink', value: 'consumerLink', label: 'Consumer app link' },
];

// Single source of truth for display labels — derived from deliveryMethodOptions so renames propagate.
const DELIVERY_METHOD_LABEL: Record<string, string> = Object.fromEntries(
  deliveryMethodOptions.map((o) => [o.value, o.label]),
);

const HOURS_PER_DAY = 24;
const POLL_INTERVAL_MS = 5000;

// Fixed row height so every row has the same spacing whether or not it renders
// the Download button (otherwise non-completed rows collapse to the bare
// line-height and crowd the row below). Cell content vertical-aligns to middle.
const ROW_STYLE = { height: '44px' };

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'danger' | 'info' | 'warning' | 'default'> = {
  completed: 'success',
  failed: 'danger',
  processing: 'info',
  pending: 'warning',
  cancelled: 'default',
};

interface JobRowProps {
  job: BulkPaymentLinkJobSummary;
  onDownload: (jobId: string) => void;
}

function JobRow({ job, onDownload }: JobRowProps) {
  const handleDownload = useCallback(() => {
    onDownload(job.jobId);
  }, [onDownload, job.jobId]);

  const requestedAt = job.requestedAt ? new Date(job.requestedAt).toLocaleString() : '—';
  const completedAt = job.completedAt ? new Date(job.completedAt).toLocaleString() : '—';

  return (
    <tr style={ROW_STYLE}>
      <td>{job.requestedByEmail ?? '—'}</td>
      <td>{requestedAt}</td>
      <td>{completedAt}</td>
      <td>{DELIVERY_METHOD_LABEL[job.deliveryMethod] ?? job.deliveryMethod}</td>
      <td>
        <Badge variant={STATUS_BADGE_VARIANT[job.status] ?? 'default'} message={job.status} />
      </td>
      <td>{job.total}</td>
      <td>{job.duplicates ?? '—'}</td>
      <td>{job.succeeded ?? '—'}</td>
      <td>{job.failed ?? '—'}</td>
      <td>
        {job.status === 'completed' && (
          <Button size="sm" variant="tertiary" type="button" onClick={handleDownload}>
            Download
          </Button>
        )}
      </td>
    </tr>
  );
}

export function BulkPaymentLinkAdmin() {
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<number>(0);

  const [createBulkPaymentLinks] = useCreateBulkPaymentLinksMutation();
  const {
    data: jobsData,
    isLoading: isLoadingJobs,
    refetch,
  } = useGetBulkPaymentLinkJobsQuery(undefined, { pollingInterval });

  useEffect(() => {
    const hasActiveJob = (jobsData?.jobs ?? []).some((j) => j.status === 'pending' || j.status === 'processing');
    setPollingInterval(hasActiveJob ? POLL_INTERVAL_MS : 0);
  }, [jobsData]);

  const handleDownload = useCallback((jobId: string) => {
    downloadFile(`/api/admin/bulk-payment-links/${jobId}/csv`, `bulk-payment-link-report-${jobId}.csv`);
  }, []);

  const handleSubmit = async (
    values: BulkPaymentLinkFormData,
    { setSubmitting, resetForm }: { setSubmitting: (v: boolean) => void; resetForm: () => void },
  ) => {
    setConfirmationMessage(null);
    try {
      let accountIds = await parseAccountIdsFromString(values.accountIds);
      accountIds = accountIds.filter((id: string) => id.trim().length > 0);

      const response = await createBulkPaymentLinks({
        accountIds,
        deliveryMethod: values.deliveryMethod,
        expirationHours:
          values.deliveryMethod === 'consumerLink' ? 24 : Number(values.expirationDays) * HOURS_PER_DAY,
      }).unwrap();

      const message = `Processing started for ${response.total} account(s). You'll receive an email with the results CSV when it's done.`;
      setConfirmationMessage(message);
      toast.success(`Processing started for ${response.total} account(s).`);
      resetForm();
      refetch();
    } catch (error: unknown) {
      if (isErrorWithData(error)) {
        toast.error(error.data.message || 'Failed to generate payment links');
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Bulk Payment Links</title>
      </Helmet>
      <MainContainer>
        <PageHeader title="Bulk Payment Links" />
        <Box padding="lg" background="white" borderRadius="md" shadow="sm">
          <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={handleSubmit}>
            {({ isSubmitting, values }) => (
              // noValidate: let Formik/yup own validation so inline errors render on submit;
              // native `required` (from isRequired) would otherwise block the submit event for
              // empty fields and suppress Formik's error display.
              <Form noValidate>
                <FormSection title="Generate Bulk Payment Links">
                  <Box childGap="md">
                    <Field
                      name="accountIds"
                      id="accountIds"
                      component={FormikTextareaInput}
                      label="Account IDs"
                      helpText="Enter FinCo account IDs separated by commas or on separate lines (max 1000)"
                      rows={8}
                      isRequired
                    />

                    <Field
                      name="deliveryMethod"
                      id="deliveryMethod"
                      component={FormikRadioGroup}
                      label="Delivery Method"
                      options={deliveryMethodOptions}
                      isRequired
                    />

                    {values.deliveryMethod !== 'consumerLink' && (
                      <Field
                        name="expirationDays"
                        id="expirationDays"
                        component={FormikTextInput}
                        type="number"
                        label="Link Expiration (days)"
                        helpText="Between 1 and 30 days"
                        isRequired
                      />
                    )}

                    <Box display="flex" gap="md" justifyContent="flex-end">
                      <Button type="submit" variant="primary" isLoading={isSubmitting} isDisabled={isSubmitting}>
                        Generate Payment Links
                      </Button>
                    </Box>
                  </Box>
                </FormSection>
              </Form>
            )}
          </Formik>

          {confirmationMessage && (
            <Box margin="lg 0 0 0" padding="md" background="primary-50" borderRadius="md">
              {confirmationMessage}
            </Box>
          )}
        </Box>

        <Box margin="lg 0 0 0" padding="lg" background="white" borderRadius="md" shadow="sm">
          <Box margin="0 0 md 0" fontWeight="bold" fontSize="lg">
            Job History
          </Box>

          {isLoadingJobs ? (
            <Box>Loading job history...</Box>
          ) : (
            <Box overflow="auto">
              <table
                aria-label="Bulk payment link job history"
                style={{ width: '100%', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                      Requested By
                    </th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                      Requested At
                    </th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                      Completed At
                    </th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                      Delivery Method
                    </th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Total</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                      Duplicates
                    </th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                      Succeeded
                    </th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Failed</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>CSV</th>
                  </tr>
                </thead>
                <tbody>
                  {jobsData?.jobs.map((job) => (
                    <JobRow key={job.jobId} job={job} onDownload={handleDownload} />
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      </MainContainer>
    </>
  );
}
